import UpgradeBanner from "../components/UpgradeBanner";
import LoadingMessages from "../components/LoadingMessages";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, useAuthStore } from "../store/authStore";
import { toast } from "sonner";
import { ArrowLeft, LinkSimple, Plus, Trash, ArrowClockwise, Spinner, ArrowSquareOut } from "@phosphor-icons/react";

const S = {
  bg:'#0a0a0f', white:'#13131f', border:'rgba(124,58,237,0.2)', borderMd:'rgba(124,58,237,0.4)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a', indigo:'#7c3aed',
};
const card = { background:S.white, border:`1px solid ${S.border}`, borderRadius:10 };

function StatusBadge({ status }) {
  const styles = {
    active:       { bg:'rgba(21,128,61,0.2)',  color:'#4ade80' },
    lost:         { bg:'rgba(185,28,28,0.2)',  color:'#f87171' },
    pending:      { bg:'rgba(90,90,122,0.2)',  color:'#9090b0' },
    error:        { bg:'rgba(161,98,7,0.2)',   color:'#fbbf24' },
    timeout:      { bg:'rgba(161,98,7,0.2)',   color:'#fbbf24' },
    source_error: { bg:'rgba(194,65,12,0.2)',  color:'#fb923c' },
  };
  const s = styles[status] || styles.pending;
  return <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:5, background:s.bg, color:s.color }}>{status}</span>;
}

function DofollowBadge({ isDofollow }) {
  if (isDofollow === null || isDofollow === undefined) return <span style={{ fontSize:11, color:S.hint }}>—</span>;
  return isDofollow
    ? <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:5, background:'rgba(21,128,61,0.2)', color:'#4ade80' }}>dofollow</span>
    : <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:5, background:S.bg, color:S.hint }}>nofollow</span>;
}

export default function BacklinkMonitor() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [projects, setProjects]       = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [backlinks, setBacklinks]     = useState([]);
  const [overview, setOverview]       = useState(null);
  const [checkStatus, setCheckStatus] = useState(null);
  const [newUrls, setNewUrls]         = useState("");
  const [loading, setLoading]         = useState(true);
  const [checking, setChecking]       = useState(false);
  const [adding, setAdding]           = useState(false);
  const [filter, setFilter]           = useState("all");

  useEffect(() => {
    apiClient.get("/projects").then(data => {
      setProjects(data);
      const pid = searchParams.get("project");
      if (pid && data.find(p=>p.project_id===pid)) setSelectedProject(pid);
      else if (data.length>0) setSelectedProject(data[0].project_id);
    }).catch(()=>toast.error("Failed to load projects")).finally(()=>setLoading(false));
  }, [searchParams]);

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const [bls, ov, st] = await Promise.all([
        apiClient.get(`/backlinks/${selectedProject}`),
        apiClient.get(`/backlinks/overview/${selectedProject}`),
        apiClient.get(`/backlinks/check/${selectedProject}/status`),
      ]);
      setBacklinks(bls); setOverview(ov); setCheckStatus(st);
    } catch(e) { console.error(e); }
  }, [selectedProject]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!checking || !selectedProject) return;
    const interval = setInterval(async () => {
      try {
        const st = await apiClient.get(`/backlinks/check/${selectedProject}/status`);
        setCheckStatus(st);
        if (st.status==="completed") { setChecking(false); toast.success(`Check complete! ${st.checked}/${st.total} verified.`); loadData(); }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [checking, selectedProject, loadData]);

  const handleAdd = async () => {
    if (!newUrls.trim() || !selectedProject) return;
    setAdding(true);
    try {
      const urls = newUrls.split("\n").map(u=>u.trim()).filter(Boolean);
      await apiClient.post("/backlinks", { project_id:selectedProject, urls });
      toast.success(`${urls.length} backlink(s) added`);
      setNewUrls(""); loadData();
    } catch(e) { toast.error(e.message||"Failed to add backlinks"); }
    finally { setAdding(false); }
  };

  const handleCheck = async () => {
    if (!selectedProject) return;
    setChecking(true);
    try { await apiClient.post(`/backlinks/check/${selectedProject}`, {}); toast.success("Backlink check started..."); }
    catch(e) { setChecking(false); toast.error(e.message||"Failed to start check"); }
  };

  const handleDelete = async (id) => {
    try { await apiClient.fetch("/backlinks", { method:"DELETE", body:JSON.stringify({ backlink_ids:[id] }) }); toast.success("Backlink removed"); loadData(); }
    catch { toast.error("Failed to delete"); }
  };

  const plan = user?.plan||"free";
  const limit = plan==="agency"?5000:plan==="pro"?1000:10;
  const filtered = backlinks.filter(bl => {
    if (filter==="all") return true;
    if (filter==="active") return bl.status==="active";
    if (filter==="lost") return bl.status==="lost";
    if (filter==="dofollow") return bl.is_dofollow===true;
    if (filter==="nofollow") return bl.is_dofollow===false;
    if (filter==="pending") return bl.status==="pending";
    return true;
  });

  const stats = overview ? [
    { label:"Total",     value:overview.total_backlinks, color:S.text },
    { label:"Active",    value:overview.active,          color:'#4ade80' },
    { label:"Lost",      value:overview.lost,            color:'#f87171' },
    { label:"Dofollow",  value:overview.dofollow,        color:'#60a5fa' },
    { label:"Nofollow",  value:overview.nofollow,        color:S.hint },
    { label:"New (7d)",  value:overview.new_last_7d,     color:'#4ade80' },
    { label:"Lost (7d)", value:overview.lost_last_7d,    color:'#f87171' },
    { label:"Pending",   value:overview.pending,         color:'#fbbf24' },
  ] : [];

  if (loading) return (
    <div style={{ minHeight:'100vh', background:S.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:40 }}>
      <div style={{ width:'100%', maxWidth:480 }}>
        <LoadingMessages />
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:S.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:S.text }}>
      <header style={{ background:S.white, borderBottom:`1px solid ${S.border}`, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px', height:52, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <button onClick={()=>navigate('/dashboard')}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'transparent', border:`1px solid ${S.border}`, borderRadius:6, fontSize:13, color:S.muted, cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=S.borderMd}
              onMouseLeave={e=>e.currentTarget.style.borderColor=S.border}>
              <ArrowLeft style={{ width:13, height:13 }} /> Dashboard
            </button>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, background:'rgba(14,165,233,0.15)', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <LinkSimple weight="bold" style={{ width:14, height:14, color:'#38bdf8' }} />
              </div>
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:S.text, margin:0, letterSpacing:'-0.02em' }}>Backlink Monitor</h1>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {projects.length>0 && (
              <select value={selectedProject||""} onChange={e=>setSelectedProject(e.target.value)}
                style={{ padding:'5px 10px', border:`1px solid ${S.border}`, borderRadius:6, fontSize:13, color:S.text, background:S.white, outline:'none' }}>
                {projects.map(p=><option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            )}
            <button onClick={handleCheck} disabled={checking||backlinks.length===0}
              style={{ display:'flex', alignItems:'center', gap:7, padding:'7px 14px', background:checking?S.hint:S.indigo, color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:checking?'not-allowed':'pointer' }}>
              {checking ? <Spinner style={{ width:13, height:13 }} className="animate-spin"/> : <ArrowClockwise weight="bold" style={{ width:13, height:13 }} />}
              {checking ? `Checking ${checkStatus?.checked||0}/${checkStatus?.total||0}...` : "Check backlinks"}
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth:1280, margin:'0 auto', padding:'32px 24px' }}>
        <UpgradeBanner feature="1000 backlinks, alertes nouvelles mentions, export" />
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:28, fontWeight:900, color:S.text, margin:0, letterSpacing:'-0.04em' }}>Backlinks</h1>
          <p style={{ fontSize:13, color:S.hint, margin:'5px 0 0' }}>Monitor every link pointing to your site — checked automatically every night at 4AM.</p>
        </div>

        {stats.length>0 && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(8,1fr)', gap:10, marginBottom:20 }}>
            {stats.map(s=>(
              <div key={s.label} style={{ ...card, padding:'12px 14px', textAlign:'center' }}>
                <p style={{ fontSize:20, fontWeight:900, color:s.color, margin:0, fontFamily:"'Satoshi', sans-serif", letterSpacing:'-0.03em' }}>{s.value??0}</p>
                <p style={{ fontSize:10, color:S.hint, margin:'3px 0 0', fontWeight:500 }}>{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {checking && <LoadingMessages style={{ marginBottom: 20 }} />}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:20, alignItems:'start' }}>
          <div style={{ ...card, overflow:'hidden' }}>
            <div style={{ padding:'12px 18px', borderBottom:`1px solid ${S.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
              <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:0 }}>
                Backlinks <span style={{ fontSize:12, fontWeight:500, color:S.hint, marginLeft:6 }}>{filtered.length}/{limit}</span>
              </p>
              <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                {["all","active","lost","dofollow","nofollow","pending"].map(f=>(
                  <button key={f} onClick={()=>setFilter(f)}
                    style={{ fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:20, border:`1px solid ${filter===f?S.indigo:S.border}`, background:filter===f?S.indigo:'transparent', color:filter===f?'#fff':S.muted, cursor:'pointer' }}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length===0 ? (
              <div style={{ padding:'48px 24px', textAlign:'center' }}>
                <LinkSimple style={{ width:28, height:28, color:S.hint, margin:'0 auto 10px' }} />
                <p style={{ fontSize:13, color:S.hint, margin:0 }}>No backlinks yet — add some on the right</p>
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:S.bg }}>
                      {['Source URL','Status','Type','Anchor','HTTP',''].map(h=>(
                        <th key={h} style={{ padding:'8px 14px', fontSize:10, fontWeight:700, color:S.hint, textAlign:h===''?'center':'left', letterSpacing:'0.06em', textTransform:'uppercase', borderBottom:`1px solid ${S.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((bl,i)=>(
                      <tr key={bl.backlink_id}
                        style={{ borderBottom:i<filtered.length-1?`1px solid ${S.border}`:'none', background:bl.status==='lost'?'rgba(248,113,113,0.07)':'transparent' }}
                        onMouseEnter={e=>{ if(bl.status!=='lost') e.currentTarget.style.background='rgba(124,58,237,0.05)'; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background=bl.status==='lost'?'rgba(248,113,113,0.07)':'transparent'; }}>
                        <td style={{ padding:'10px 14px', maxWidth:260 }}>
                          <a href={bl.source_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize:11, color:S.indigo, textDecoration:'none', wordBreak:'break-all', lineHeight:1.4 }}>{bl.source_url}</a>
                        </td>
                        <td style={{ padding:'10px 14px' }}><StatusBadge status={bl.status} /></td>
                        <td style={{ padding:'10px 14px' }}><DofollowBadge isDofollow={bl.is_dofollow} /></td>
                        <td style={{ padding:'10px 14px', fontSize:11, color:S.muted, maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{bl.anchor_text||'—'}</td>
                        <td style={{ padding:'10px 14px', fontSize:11, color:S.hint, textAlign:'center' }}>{bl.http_status||'—'}</td>
                        <td style={{ padding:'10px 14px', textAlign:'center' }}>
                          <button onClick={()=>handleDelete(bl.backlink_id)}
                            style={{ padding:4, background:'transparent', border:'none', cursor:'pointer', color:S.hint, borderRadius:4 }}
                            onMouseEnter={e=>e.currentTarget.style.color='#f87171'}
                            onMouseLeave={e=>e.currentTarget.style.color=S.hint}>
                            <Trash style={{ width:13, height:13 }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ ...card, padding:'16px 18px' }}>
              <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:'0 0 4px', letterSpacing:'-0.01em' }}>Add backlinks</p>
              <p style={{ fontSize:12, color:S.hint, margin:'0 0 12px' }}>{backlinks.length}/{limit} used · one URL per line</p>
              <textarea value={newUrls} onChange={e=>setNewUrls(e.target.value)}
                placeholder={"https://blog.example.com/article\nhttps://directory.com/listing"}
                style={{ width:'100%', height:130, padding:'10px 12px', background:S.bg, border:`1px solid ${S.border}`, borderRadius:7, fontSize:12, color:S.text, fontFamily:'inherit', resize:'none', outline:'none', boxSizing:'border-box', lineHeight:1.6 }}
                onFocus={e=>e.target.style.borderColor=S.indigo}
                onBlur={e=>e.target.style.borderColor=S.border}
              />
              <button onClick={handleAdd} disabled={adding||!newUrls.trim()}
                style={{ width:'100%', marginTop:10, padding:'9px 0', background:adding?S.hint:S.indigo, color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:adding?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
                {adding ? <Spinner style={{ width:13, height:13 }} className="animate-spin"/> : <Plus weight="bold" style={{ width:13, height:13 }} />}
                {adding ? 'Adding...' : 'Add backlinks'}
              </button>
            </div>

            <div style={{ ...card, padding:'14px 16px' }}>
              <p style={{ fontSize:11, fontWeight:700, color:S.hint, margin:'0 0 10px', letterSpacing:'0.06em', textTransform:'uppercase' }}>How it works</p>
              <ul style={{ margin:0, padding:'0 0 0 14px', fontSize:12, color:S.muted, lineHeight:1.9 }}>
                <li>Add URLs of pages linking to you</li>
                <li>We verify each link every night at 4AM</li>
                <li>Email alert if a backlink is lost</li>
                <li>Track dofollow vs nofollow status</li>
              </ul>
            </div>

            {checkStatus?.last_check && (
              <div style={{ ...card, padding:'12px 16px' }}>
                <p style={{ fontSize:11, color:S.hint, margin:0 }}>
                  Last check: <strong style={{ color:S.muted }}>{new Date(checkStatus.last_check).toLocaleString('en',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</strong>
                </p>
              </div>
            )}

            {plan==='free' && backlinks.length>=8 && (
              <div style={{ background:'rgba(124,58,237,0.12)', border:'1px solid rgba(124,58,237,0.35)', borderRadius:10, padding:'14px 16px' }}>
                <p style={{ fontSize:12, fontWeight:700, color:'#a78bfa', margin:'0 0 4px' }}>Free plan: {limit} backlinks</p>
                <p style={{ fontSize:11, color:S.indigo, margin:'0 0 10px' }}>Upgrade to Pro for 1,000 backlinks</p>
                <button onClick={()=>navigate('/pricing')}
                  style={{ width:'100%', padding:'7px 0', background:S.indigo, color:'#fff', border:'none', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Upgrade to Pro →
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
